/**
 * Serializes the PUT /api/checkout/setShippingOption calls triggered by the nShift widget.
 *
 * The widget invokes its result callback on every keystroke in its own input fields (for
 * example the notification phone number), so a single phone number produces several results.
 * Sending them all in parallel means their responses can complete out of order - the server
 * persists whichever finishes last - and an early request carrying a half-typed number such as
 * "0735" could overwrite the complete one. The customer then never gets a delivery
 * notification and the number has to be corrected manually with the carrier.
 *
 * Only one request per delivery group is in flight at a time here, and while one is running
 * only the most recent result is kept, so the value the customer actually finished typing is
 * always the one that wins.
 */

const queues = {};

const getQueue = (key) => {
    if (!queues[key]) {
        queues[key] = { tail: Promise.resolve(), pending: null };
    }
    return queues[key];
};

/**
 * Runs `run` once any earlier update for the same key has finished. If several updates are
 * queued while a request is in flight, only the latest one is sent.
 *
 * @param {string} key - Delivery group key ('' for the single-widget flow)
 * @param {Function} run - Returns a promise for the update request
 * @returns {Promise} Resolves when this step of the queue has been processed
 */
export const queueShippingUpdate = (key, run) => {
    const queue = getQueue(key);
    queue.pending = run;

    queue.tail = queue.tail
        .catch(() => {})
        .then(() => {
            const next = queue.pending;
            // Already handled by an earlier step that picked up a newer result.
            if (!next) {
                return undefined;
            }
            queue.pending = null;
            return next();
        });

    return queue.tail;
};

const pendingResults = new Set();

/**
 * Registers a debounced widget-result dispatcher so it can be flushed before the order is
 * placed. Returns an unregister function for component cleanup.
 *
 * @param {Function} debounced - lodash debounced function
 * @returns {Function} Unregister callback
 */
export const registerPendingShippingResult = (debounced) => {
    pendingResults.add(debounced);
    return () => pendingResults.delete(debounced);
};

/**
 * Sends any debounced widget result immediately and waits for every queued and in-flight
 * update to finish, so the order is placed with the values the customer last entered.
 *
 * @returns {Promise} Resolves when all pending shipping updates have been persisted
 */
export const flushShippingUpdates = () => {
    // Never let a failing update block the order from being placed - the server keeps the
    // previously saved selection in that case.
    try {
        pendingResults.forEach((debounced) => debounced.flush());
    } catch (ex) {
        return Promise.resolve();
    }

    return Promise.all(
        Object.keys(queues).map((key) => queues[key].tail.catch(() => {}))
    );
};
